import {
    Box,
    Button,
    Checkbox,
    Flex,
    Heading,
    HStack,
    IconButton,
    Menu,
    MenuButton,
    MenuItem,
    MenuList,
    Radio,
    RadioGroup,
    Slider,
    SliderFilledTrack,
    SliderMark,
    SliderThumb,
    SliderTrack,
    Stack,
    Text,
    Tooltip
} from "@chakra-ui/react";
import React, {useCallback, useContext, useEffect, useMemo, useRef, useState} from "react";
import {AppContext, AppContextI} from "../context/ContextWrapper";
import {UploadFileMetadata} from "../upload/Upload";
import {useUploadQueueContext} from "../uploadQueue/UploadQueueContext";
import {Area, SoundFile, SoundFileLibraryEntry, Spot} from "../types";
import SoundFileDetails from "../spotList/details/SoundFileDetails";
import {useLazyQuery, useMutation, useQuery} from "@apollo/client";
import {registerSoundFileMutation, soundFileLibraryQuery} from "./soundFileQueries";
import {UserContext, UserContextI} from "../../UserContext";
import SoundFileLibraryDialog, {ExistingSoundOption} from "./SoundFileLibraryDialog";
import {versionsQuery} from "../../pages/versions/versionQueries";
import {getSpotsQuery} from "../spots/graphQl";
import {getAreaContentQuery} from "../area/areaQueries";
import HelpTooltip from "../help/HelpTooltip";
import {BiTrash} from "react-icons/bi";

export type LanguageOption = {
    code: string
    label: string
    isDefault?: boolean
}

type Props = {
    spot: Spot
    title: string
    isAmbient: boolean
    languages: LanguageOption[]
}

const isDefaultLanguageValue = (languageValue?: string | null) => {
    return !(languageValue || "").trim();
};

const SoundSlotSection = ({spot, title, isAmbient, languages}: Props) => {
    const appContext: AppContextI = useContext<AppContextI>(AppContext);
    const slotSoundFiles = useMemo(() => {
        return (spot.soundFiles || []).filter((soundFile) => (soundFile.ambient ?? false) === isAmbient);
    }, [isAmbient, spot.soundFiles]);
    const explicitDefaultLanguageCode = useMemo(() => {
        return languages.find((language) => language.isDefault)?.code;
    }, [languages]);
    const firstAvailableLanguageCode = useMemo(() => {
        return slotSoundFiles.find((soundFile) => !isDefaultLanguageValue(soundFile.language))?.language;
    }, [slotSoundFiles]);
    const hasSlotLevelDefaultSound = useMemo(() => {
        return slotSoundFiles.some((soundFile) => isDefaultLanguageValue(soundFile.language));
    }, [slotSoundFiles]);
    const resolvedDefaultLanguageCode = explicitDefaultLanguageCode || firstAvailableLanguageCode || languages[0]?.code;
    const defaultLanguage = useMemo(() => {
        return languages.find((language) => language.code === resolvedDefaultLanguageCode) || languages[0];
    }, [languages, resolvedDefaultLanguageCode]);
    const defaultRow = useMemo(() => {
        const base = defaultLanguage || {code: "EN", label: "English"};
        return {
            ...base,
            label: `Default (${base.label})`,
            isDefault: true
        };
    }, [defaultLanguage]);
    const defaultRowUsesLanguageCode = !hasSlotLevelDefaultSound && !!resolvedDefaultLanguageCode;
    const getDisplayLanguageLabel = useCallback((languageValue?: string | null) => {
        if (isDefaultLanguageValue(languageValue)) {
            return defaultLanguage?.label || defaultRow.label;
        }
        return languageValue as string;
    }, [defaultLanguage?.label, defaultRow.label]);
    const availableLocalized = useMemo(() => {
        if (!defaultLanguage) {
            return languages;
        }
        return languages.filter((language) => language.code !== defaultLanguage.code);
    }, [defaultLanguage, languages]);
    const existingLocalizedCodes = useMemo(() => {
        return (spot.soundFiles || [])
            .filter((soundFile) => (soundFile.ambient ?? false) === isAmbient && !isDefaultLanguageValue(soundFile.language))
            .map((soundFile) => soundFile.language as string);
    }, [isAmbient, spot.soundFiles]);
    const [addedLocalizedCodes, setAddedLocalizedCodes] = useState<string[]>([]);
    useEffect(() => {
        setAddedLocalizedCodes([]);
    }, [spot.id, isAmbient]);
    const visibleLocalizedCodes = useMemo(() => {
        return new Set([...existingLocalizedCodes, ...addedLocalizedCodes]);
    }, [existingLocalizedCodes, addedLocalizedCodes]);
    const visibleLocalizedLanguages = useMemo(() => {
        return availableLocalized.filter((language) => visibleLocalizedCodes.has(language.code));
    }, [availableLocalized, visibleLocalizedCodes]);
    const remainingLocalizedLanguages = useMemo(() => {
        return availableLocalized.filter((language) => !visibleLocalizedCodes.has(language.code));
    }, [availableLocalized, visibleLocalizedCodes]);
    const hasAmbientSound = useMemo(() => {
        return (spot.soundFiles || []).some((soundFile) => (soundFile.ambient ?? false));
    }, [spot.soundFiles]);
    const getAmbientModeFromSpot = useCallback((): "specific" | "pause" | "duck" => {
        if (spot.pauseOtherAmbients === true) {
            return "pause";
        }
        if (spot.duckOtherAmbients === true) {
            return "duck";
        }
        if (spot.pauseOtherAmbients === false) {
            return "specific";
        }
        return hasAmbientSound ? "specific" : "pause";
    }, [hasAmbientSound, spot.duckOtherAmbients, spot.pauseOtherAmbients]);
    const [ambientMode, setAmbientMode] = useState<"specific" | "pause" | "duck">(() => getAmbientModeFromSpot());
    const [duckOtherAmbientsDb, setDuckOtherAmbientsDb] = useState<number>(spot.duckOtherAmbientsDb ?? -6);
    const [pendingDuckOtherAmbientsDb, setPendingDuckOtherAmbientsDb] = useState<number>(spot.duckOtherAmbientsDb ?? -6);
    const [loadSoundFileLibrary, {
        data: soundFileData,
        loading: soundFileLoading
    }] = useLazyQuery(soundFileLibraryQuery, {
        fetchPolicy: "cache-first"
    });
    const {data: eagerSoundFileData} = useQuery(soundFileLibraryQuery, {
        variables: {areaId: appContext.getArea()?.id},
        skip: !appContext.getArea()?.id,
        fetchPolicy: "cache-first"
    });
    const libraryEntriesForLookup: SoundFileLibraryEntry[] = useMemo(() => {
        return soundFileData?.getSoundFileLibrary || eagerSoundFileData?.getSoundFileLibrary || [];
    }, [soundFileData, eagerSoundFileData]);
    const libraryByObjectId = useMemo(() => {
        const map = new Map<string, SoundFileLibraryEntry>();
        libraryEntriesForLookup.forEach((entry) => {
            if (entry.objectId) {
                map.set(entry.objectId, entry);
            }
        });
        return map;
    }, [libraryEntriesForLookup]);
    const isSyncingAmbientMode = useRef<boolean>(false);
    const suppressDuckDbUpdate = useRef<boolean>(false);
    const ambientModeRef = useRef<"specific" | "pause" | "duck">(ambientMode);
    ambientModeRef.current = ambientMode;

    const areaId = appContext.getArea()?.id;
    const [shouldLoadUsage, setShouldLoadUsage] = useState<boolean>(false);
    const {data: versionData} = useQuery(versionsQuery, {
        variables: {areaId: areaId},
        skip: !areaId || !shouldLoadUsage
    });
    const latestPublishedAt = useMemo(() => {
        const versions = versionData?.getVersions || [];
        const published = versions.filter((version: any) => typeof version?.publishedAt === "number" && version.publishedAt > 0);
        if (published.length === 0) {
            return undefined;
        }
        return published.reduce((max: number, version: any) => Math.max(max, version.publishedAt), 0);
    }, [versionData]);
    const hasDraftVersion = useMemo(() => {
        const versions = versionData?.getVersions || [];
        return versions.some((version: any) => version?.publishedAt === 0);
    }, [versionData]);
    const needsPublished = shouldLoadUsage && !!latestPublishedAt;
    const needsDraft = shouldLoadUsage && hasDraftVersion;
    const {data: publishedSpotData} = useQuery(getSpotsQuery, {
        variables: {areaId: areaId, publishedAt: latestPublishedAt || 0},
        skip: !areaId || !needsPublished || !latestPublishedAt
    });
    const {data: publishedAreaData} = useQuery(getAreaContentQuery, {
        variables: {areaId: areaId, publishedAt: latestPublishedAt || 0},
        skip: !areaId || !needsPublished || !latestPublishedAt
    });
    const {data: draftSpotData} = useQuery(getSpotsQuery, {
        variables: {areaId: areaId, publishedAt: 0},
        skip: !areaId || !needsDraft
    });
    const {data: draftAreaData} = useQuery(getAreaContentQuery, {
        variables: {areaId: areaId, publishedAt: 0},
        skip: !areaId || !needsDraft
    });
    const publishedSpots = useMemo<Spot[]>(() => {
        return publishedSpotData?.getSpots || [];
    }, [publishedSpotData]);
    const draftSpots = useMemo<Spot[]>(() => {
        return draftSpotData?.getSpots || [];
    }, [draftSpotData]);
    const publishedArea = publishedAreaData?.getAreaContent;
    const draftArea = draftAreaData?.getAreaContent;

    useEffect(() => {
        if (!isAmbient) {
            return;
        }
        const nextMode = getAmbientModeFromSpot();
        if (nextMode !== ambientModeRef.current) {
            isSyncingAmbientMode.current = true;
            setAmbientMode(nextMode);
        }
        const nextDb = spot.duckOtherAmbientsDb ?? -6;
        setDuckOtherAmbientsDb(nextDb);
        setPendingDuckOtherAmbientsDb(nextDb);
    }, [
        getAmbientModeFromSpot,
        hasAmbientSound,
        isAmbient,
        spot.duckOtherAmbients,
        spot.duckOtherAmbientsDb,
        spot.id,
        spot.pauseOtherAmbients
    ]);

    useEffect(() => {
        if (!isAmbient) {
            return;
        }
        if (isSyncingAmbientMode.current) {
            isSyncingAmbientMode.current = false;
            return;
        }
        const nextDuck = ambientMode === "duck";
        const nextPause = ambientMode === "pause";
        const currentDuck = spot.duckOtherAmbients === true;
        const currentPause = spot.pauseOtherAmbients === true;
        const shouldUpdateDuck = nextDuck !== currentDuck;
        const shouldUpdatePause = nextPause !== currentPause;
        if (!shouldUpdateDuck && !shouldUpdatePause) {
            return;
        }
        if (nextDuck && !currentDuck) {
            suppressDuckDbUpdate.current = true;
        }
        appContext.updateSpot(spot, {
            ...spot,
            duckOtherAmbients: nextDuck,
            pauseOtherAmbients: nextPause,
            duckOtherAmbientsDb: nextDuck ? duckOtherAmbientsDb : spot.duckOtherAmbientsDb
        } as Spot);
    }, [ambientMode, duckOtherAmbientsDb, isAmbient, spot, appContext]);

    useEffect(() => {
        if (!isAmbient || ambientMode !== "duck") {
            return;
        }
        if (suppressDuckDbUpdate.current) {
            suppressDuckDbUpdate.current = false;
            return;
        }
        const currentDb = spot.duckOtherAmbientsDb;
        if (duckOtherAmbientsDb === currentDb) {
            return;
        }
        appContext.updateSpot(spot, {
            ...spot,
            duckOtherAmbientsDb: duckOtherAmbientsDb
        } as Spot);
    }, [ambientMode, duckOtherAmbientsDb, isAmbient, spot, appContext]);

    const usageMap = useMemo(() => {
        type UsageEntry = {
            label: string
            languageLabel: string
            ambient: boolean
            versions: Set<string>
        };
        const dedupMap = new Map<string, { sound: SoundFile; usages: Map<string, UsageEntry> }>();
        const getUsageKey = (label: string, languageLabel: string, ambient: boolean) => {
            return `${label}||${languageLabel}||${ambient ? "ambient" : "base"}`;
        };
        const addUsage = (fileName: string | undefined, sound: SoundFile, label: string, versionLabel: string) => {
            if (!fileName || !label) {
                return;
            }
            const languageLabel = getDisplayLanguageLabel(sound.language);
            const ambient = !!sound.ambient;
            const usageKey = getUsageKey(label, languageLabel, ambient);
            const existing = dedupMap.get(fileName) || {sound: sound, usages: new Map<string, UsageEntry>()};
            const usageEntry = existing.usages.get(usageKey) || {
                label: label,
                languageLabel: languageLabel,
                ambient: ambient,
                versions: new Set<string>()
            };
            usageEntry.versions.add(versionLabel);
            existing.usages.set(usageKey, usageEntry);
            dedupMap.set(fileName, existing);
        };

        const appendSpotUsage = (spots: Spot[], versionLabel: string) => {
            spots.forEach((s) => {
                const spotLabel = s.name || "Untitled spot";
                (s.soundFiles || []).forEach((sf) => {
                    addUsage(sf.fileName, sf, spotLabel, versionLabel);
                });
            });
        };

        const appendZoneUsage = (areaSource: Area | any, versionLabel: string) => {
            const zones = areaSource?.zones || {};
            const baseZones = Object.entries(zones)
                .filter(([zoneId, zone]) => zoneId !== "ambientZones" && zone)
                .map(([, zone]) => zone);
            const ambientZones = Array.isArray(zones.ambientZones) ? zones.ambientZones : [];
            const allZones = [...baseZones, ...ambientZones].filter((zone: any) => zone);
            allZones.forEach((zone: any) => {
                const rawLabel = zone?.label || zone?.name || zone?.id;
                if (!rawLabel) {
                    return;
                }
                const normalizedLabel = rawLabel.charAt(0).toUpperCase() + rawLabel.slice(1);
                const zoneLabel = `Zone: ${normalizedLabel}`;
                const zoneSoundFiles = zone?.soundFiles ? Object.values(zone.soundFiles) as SoundFile[] : [];
                zoneSoundFiles.forEach((zoneSound) => {
                    if (zoneSound) {
                        addUsage(zoneSound.fileName, zoneSound, zoneLabel, versionLabel);
                    }
                });
            });
        };

        if (needsPublished) {
            appendSpotUsage(publishedSpots, "Published");
            appendZoneUsage(publishedArea, "Published");
        }
        if (needsDraft) {
            appendSpotUsage(draftSpots, "Draft");
            appendZoneUsage(draftArea, "Draft");
        }
        const flattened = new Map<string, ExistingSoundOption>();
        const versionOrder = ["Published", "Draft"];
        dedupMap.forEach((value, fileName) => {
            const spotNames = Array.from(value.usages.values()).map((usage) => {
                const versions = versionOrder.filter((ver) => usage.versions.has(ver));
                const versionLabel = versions.length > 0 ? versions.join(" + ") : "Unknown";
                const parts = [usage.languageLabel];
                if (usage.ambient) {
                    parts.push("ambient");
                }
                parts.push(versionLabel);
                return `${usage.label} (${parts.join(", ")})`;
            });
            flattened.set(fileName, {
                sound: value.sound,
                spotNames: spotNames
            });
        });
        return flattened;
    }, [draftArea, draftSpots, getDisplayLanguageLabel, needsDraft, needsPublished, publishedArea, publishedSpots]);

    const existingSoundOptions = useMemo(() => {
        if (!soundFileData) {
            return [];
        }
        const libraryEntries: SoundFileLibraryEntry[] = soundFileData?.getSoundFileLibrary || [];
        const options: ExistingSoundOption[] = [];

        libraryEntries.forEach((entry) => {
            const fileName = entry.objectId;
            if (!fileName) {
                return;
            }
            const usage = usageMap.get(fileName);
            const sound: SoundFile = usage?.sound || {
                originalFileName: entry.originalFileName,
                fileName: fileName,
                filePath: entry.filePath,
                playOnes: true,
                fadeIn: 0,
                fadeOut: 0,
                delay: 0,
                synchronize: false,
                noSyncTime: 0,
                volumeDb: 0
            };
            options.push({
                sound: sound,
                spotNames: usage?.spotNames || [],
                libraryEntry: entry
            });
        });

        return options;
    }, [soundFileData, usageMap]);

    const requestSoundFileLibrary = useCallback(() => {
        const areaId = appContext.getArea()?.id;
        if (!areaId) {
            return;
        }
        setShouldLoadUsage(true);
        loadSoundFileLibrary({
            variables: {
                areaId: areaId
            }
        });
    }, [appContext, loadSoundFileLibrary]);

    const canAddLocalized = remainingLocalizedLanguages.length > 0;

    const renderSoundFiles = () => (
        <Stack spacing={4}>
                            <SoundSlotLanguageRow
                                key={`${title}-default`}
                                spot={spot}
                                language={defaultRow}
                                isAmbient={isAmbient}
                                existingSoundOptions={existingSoundOptions}
                                onRequestLibrary={requestSoundFileLibrary}
                                isLibraryLoading={soundFileLoading}
                                resolvedDefaultLanguageCode={resolvedDefaultLanguageCode}
                                defaultRowUsesLanguageCode={defaultRowUsesLanguageCode}
                                libraryByObjectId={libraryByObjectId}
                            />
                            {availableLocalized.length > 0 ? (
                <Box>
                    <HStack justifyContent={"space-between"} marginBottom={2}>
                        <Text fontWeight={"semibold"} fontSize={"sm"}>Localized versions</Text>
                        <Menu>
                            <MenuButton as={Button} size={"xs"} variant={"outline"}
                                        isDisabled={!canAddLocalized || !appContext.isDraft()}>
                                Add localized version
                            </MenuButton>
                            <MenuList maxH={"60vh"} overflowY={"auto"} overflowX={"hidden"}>
                                {remainingLocalizedLanguages.map((language) => (
                                    <MenuItem
                                        key={`add-${language.code}`}
                                        onClick={() => setAddedLocalizedCodes((prev) => prev.includes(language.code)
                                            ? prev
                                            : [...prev, language.code])}
                                    >
                                        {language.label}
                                    </MenuItem>
                                ))}
                            </MenuList>
                        </Menu>
                    </HStack>
                    <Stack spacing={4}>
                        {visibleLocalizedLanguages.length > 0 ? (
                            visibleLocalizedLanguages.map((language) => (
                                <SoundSlotLanguageRow
                                    key={`${title}-${language.code}`}
                                    spot={spot}
                                    language={language}
                                    isAmbient={isAmbient}
                                    existingSoundOptions={existingSoundOptions}
                                    onRequestLibrary={requestSoundFileLibrary}
                                    isLibraryLoading={soundFileLoading}
                                    resolvedDefaultLanguageCode={resolvedDefaultLanguageCode}
                                    defaultRowUsesLanguageCode={false}
                                    libraryByObjectId={libraryByObjectId}
                                />
                            ))
                        ) : (
                            <Text fontSize={"sm"} color={"gray.600"}>No localized versions added.</Text>
                        )}
                    </Stack>
                </Box>
            ) : null}
        </Stack>
    );

    return <Box marginBottom={"8"}>
        <Heading size={"sm"} marginBottom={2}>{title}</Heading>
        {isAmbient ? (
            <>
                <Text marginBottom={2}>Common ambient sounds are defined in the Zones - Ambient section</Text>
                <Checkbox
                    isChecked={spot.followMainSound === true}
                    isDisabled={!appContext.isDraft()}
                    onChange={(event) => {
                        appContext.updateSpot(spot, {
                            ...spot,
                            followMainSound: event.target.checked,
                        } as Spot);
                    }}
                    marginBottom={3}
                >
                    Follow main sound
                    <HelpTooltip
                        content={"When on, this spot's ambient starts and stops together with the main sound: it begins when the main sound starts and ends when the main sound finishes or is stopped."}
                        placement={"right"}
                        icon
                        iconAriaLabel={"Follow main sound help"}
                    />
                </Checkbox>
                <RadioGroup value={ambientMode} onChange={(val) => {
                    setAmbientMode(val as "specific" | "pause" | "duck");
                }}>
                    <Stack spacing={4}>
                        <Radio value={"pause"} isDisabled={!appContext.isDraft()}>
                            Pause other ambients
                        </Radio>
                        <Radio value={"duck"} isDisabled={!appContext.isDraft()}>
                            Duck other ambients
                        </Radio>
                        {ambientMode === "duck" ? (
                            <Box paddingLeft={6}>
                                <Text fontWeight={"semibold"} mb={2}>
                                    Duck by (dB)
                                    <HelpTooltip
                                        content={"Applies an additional attenuation relative to current ambient level."}
                                        placement={"right"}
                                        icon
                                        iconAriaLabel={"Duck other ambients help"}
                                    />
                                </Text>
                                <Box paddingInlineEnd={8} paddingBottom={4}>
                                    <Slider
                                        aria-label={"Duck other ambients level in dB"}
                                        min={0}
                                        max={24}
                                        step={1}
                                        value={Math.abs(pendingDuckOtherAmbientsDb)}
                                        onChange={(val) => setPendingDuckOtherAmbientsDb(-val)}
                                        onChangeEnd={(val) => setDuckOtherAmbientsDb(-val)}
                                        isDisabled={!appContext.isDraft()}
                                    >
                                        {Array.from({length: 25}, (_, idx) => idx).map((val) => (
                                            <SliderMark
                                                key={`duck-tick-${val}`}
                                                value={val}
                                                boxSize={1}
                                                borderRadius={"full"}
                                                bg={"gray.300"}
                                                mt={3}
                                            />
                                        ))}
                                        <SliderMark value={0} mt={3} fontSize={"xs"} color={"gray.600"}>0</SliderMark>
                                        <SliderMark value={24} mt={3} fontSize={"xs"} color={"gray.600"}>24</SliderMark>
                                        <SliderTrack bg={"gray.200"}>
                                            <SliderFilledTrack bg={"blue.500"}/>
                                        </SliderTrack>
                                        <SliderThumb/>
                                    </Slider>
                                </Box>
                            </Box>
                        ) : null}
                        <Radio value={"specific"} isDisabled={!appContext.isDraft()}>
                            Specific ambient sound
                        </Radio>
                        {ambientMode === "specific" ? renderSoundFiles() : null}
                    </Stack>
                </RadioGroup>
            </>
        ) : (
            renderSoundFiles()
        )}
    </Box>;
};

type RowProps = {
    spot: Spot
    language: LanguageOption
    isAmbient: boolean
    existingSoundOptions: ExistingSoundOption[]
    onRequestLibrary: () => void
    isLibraryLoading: boolean
    resolvedDefaultLanguageCode?: string
    defaultRowUsesLanguageCode?: boolean
    libraryByObjectId: Map<string, SoundFileLibraryEntry>
}

const SoundSlotLanguageRow = ({
                                  spot,
                                  language,
                                  isAmbient,
                                  existingSoundOptions,
                                  onRequestLibrary,
                                  isLibraryLoading,
                                  resolvedDefaultLanguageCode,
                                  defaultRowUsesLanguageCode,
                                  libraryByObjectId
                              }: RowProps) => {
    const appContext: AppContextI = useContext<AppContextI>(AppContext);
    const userContext: UserContextI = useContext<UserContextI>(UserContext);
    const {enqueueUploads} = useUploadQueueContext();
    const [isDialogOpen, setDialogOpen] = useState<boolean>(false);
    const [selectedExistingFile, setSelectedExistingFile] = useState<string | undefined>(undefined);
    const [registerSoundFile] = useMutation(registerSoundFileMutation);

    useEffect(() => {
        if (isDialogOpen) {
            onRequestLibrary();
        }
    }, [isDialogOpen, onRequestLibrary]);

    useEffect(() => {
        if (isDialogOpen && !selectedExistingFile && existingSoundOptions.length > 0) {
            setSelectedExistingFile(existingSoundOptions[0].sound.fileName);
        }
    }, [existingSoundOptions, isDialogOpen, selectedExistingFile]);

    useEffect(() => {
        if (!isDialogOpen || !selectedExistingFile || existingSoundOptions.length === 0) {
            return;
        }
        const exists = existingSoundOptions.some((opt) => opt.sound.fileName === selectedExistingFile);
        if (!exists) {
            setSelectedExistingFile(existingSoundOptions[0].sound.fileName);
        }
    }, [existingSoundOptions, isDialogOpen, selectedExistingFile]);

    const isDefaultRow = !!language.isDefault;
    const matchesRowLanguage = useCallback((languageValue: string | null | undefined) => {
        if (isDefaultRow) {
            if (isDefaultLanguageValue(languageValue)) {
                return true;
            }
            if (defaultRowUsesLanguageCode && resolvedDefaultLanguageCode) {
                return languageValue === resolvedDefaultLanguageCode;
            }
            return false;
        }
        return languageValue === language.code;
    }, [defaultRowUsesLanguageCode, isDefaultRow, language.code, resolvedDefaultLanguageCode]);
    const currentSoundFile = useMemo(() => {
        return (spot.soundFiles || []).find((soundFile) => {
            const matchesAmbient = (soundFile.ambient ?? false) === isAmbient;
            const matchesLanguage = matchesRowLanguage(soundFile.language);
            return matchesAmbient && matchesLanguage;
        });
    }, [spot.soundFiles, isAmbient, matchesRowLanguage]);

    const updateSoundFiles = (nextSoundFile: SoundFile) => {
        const selection = appContext.getSelection();
        if (!selection) {
            return;
        }
        const soundFiles = spot.soundFiles || [];
        const filtered = soundFiles.filter((soundFile) => {
            const matchesAmbient = (soundFile.ambient ?? false) === isAmbient;
            const matchesLanguage = matchesRowLanguage(soundFile.language);
            return !(matchesAmbient && matchesLanguage);
        });

        appContext.updateSpot(spot, {
            ...spot,
            soundFiles: [...filtered, nextSoundFile]
        } as Spot);
    };

    const getDefaults = (): SoundFile => {
        const defaults: SoundFile = {
            ambient: isAmbient,
            playOnes: true,
            fadeIn: 0,
            fadeOut: 0,
            delay: 0,
            synchronize: false,
            noSyncTime: 0,
            volumeDb: 0
        };
        if (!isDefaultRow) {
            defaults.language = language.code;
        } else if (defaultRowUsesLanguageCode && resolvedDefaultLanguageCode) {
            defaults.language = resolvedDefaultLanguageCode;
        }
        return defaults;
    };

    const addOrReplaceSoundFile = (originalFileName: string, fileName: string, filePath: string, metadata?: UploadFileMetadata) => {
        setDialogOpen(false);
        if (!appContext.getSelection()) {
            return;
        }
        const nextSoundFile: SoundFile = {
            ...getDefaults(),
            ...(currentSoundFile || {}),
            originalFileName: originalFileName,
            fileName: fileName,
            filePath: filePath,
            ambient: isAmbient
        };
        if (isDefaultRow && !(defaultRowUsesLanguageCode && resolvedDefaultLanguageCode)) {
            delete nextSoundFile.language;
        } else if (isDefaultRow && resolvedDefaultLanguageCode) {
            nextSoundFile.language = resolvedDefaultLanguageCode;
        } else {
            nextSoundFile.language = language.code;
        }
        updateSoundFiles(nextSoundFile);

        const areaId = appContext.getArea()?.id;
        const uploadedBy = userContext.getUser()?.email || userContext.getUser()?.fullName;
        if (areaId) {
            registerSoundFile({
                variables: {
                    areaId: areaId,
                    objectId: fileName,
                    originalFileName: originalFileName,
                    filePath: filePath,
                    uploadedBy: uploadedBy,
                    mimeType: metadata?.mimeType,
                    sizeBytes: metadata?.sizeBytes,
                    durationMs: metadata?.durationMs
                }
            }).catch((error) => console.warn("Unable to register sound file", error));
        }
    };

    const registerUploadedFile = async (originalFileName: string, fileName: string, filePath: string, metadata?: UploadFileMetadata) => {
        const areaId = appContext.getArea()?.id;
        if (!areaId) {
            return;
        }
        const uploadedBy = userContext.getUser()?.email || userContext.getUser()?.fullName;
        try {
            await registerSoundFile({
                variables: {
                    areaId: areaId,
                    objectId: fileName,
                    originalFileName: originalFileName,
                    filePath: filePath,
                    uploadedBy: uploadedBy,
                    mimeType: metadata?.mimeType,
                    sizeBytes: metadata?.sizeBytes,
                    durationMs: metadata?.durationMs
                }
            });
        } catch (error) {
            console.warn("Unable to register sound file", error);
        }
        onRequestLibrary();
    };

    const openDialog = (preferredFileName?: string) => {
        if (preferredFileName) {
            setSelectedExistingFile(preferredFileName);
        } else if (!selectedExistingFile && existingSoundOptions.length > 0) {
            setSelectedExistingFile(existingSoundOptions[0].sound.fileName);
        }
        setDialogOpen(true);
    };

    const addExistingSoundFile = () => {
        if (!selectedExistingFile) return;
        const selected = existingSoundOptions.find((opt) => opt.sound.fileName === selectedExistingFile);
        if (!selected) return;
        const selection = appContext.getSelection();
        if (!selection) return;
        const nextSoundFile: SoundFile = {
            ...getDefaults(),
            ...(currentSoundFile || {}),
            originalFileName: selected.sound.originalFileName,
            fileName: selected.sound.fileName,
            filePath: selected.sound.filePath,
            ambient: isAmbient
        };
        if (isDefaultRow && !(defaultRowUsesLanguageCode && resolvedDefaultLanguageCode)) {
            delete nextSoundFile.language;
        } else if (isDefaultRow && resolvedDefaultLanguageCode) {
            nextSoundFile.language = resolvedDefaultLanguageCode;
        } else {
            nextSoundFile.language = language.code;
        }
        updateSoundFiles(nextSoundFile);
        setDialogOpen(false);
    };

    const removeCurrentLocalizedSoundFile = () => {
        if (!currentSoundFile || isDefaultRow) {
            return;
        }
        const selection = appContext.getSelection();
        if (!selection) {
            return;
        }
        const soundFiles = spot.soundFiles || [];
        const filtered = soundFiles.filter((soundFile) => {
            const matchesAmbient = (soundFile.ambient ?? false) === isAmbient;
            const matchesLanguage = soundFile.language === language.code;
            return !(matchesAmbient && matchesLanguage);
        });
        appContext.updateSpot(spot, {
            ...spot,
            soundFiles: filtered
        } as Spot);
    };

    return <Box borderWidth={"1px"} borderColor={"gray.200"} borderRadius={"md"} padding={3}>
        <Flex align={"center"} justify={"space-between"} marginBottom={2}>
            <Text fontWeight={"semibold"}>{language.label}</Text>
            {!isDefaultRow && currentSoundFile ? (
                <Tooltip label={"Remove localized sound file"} hasArrow>
                    <IconButton
                        aria-label={`Remove ${language.label} sound file`}
                        icon={<BiTrash/>}
                        size={"xs"}
                        variant={"ghost"}
                        colorScheme={"red"}
                        onClick={removeCurrentLocalizedSoundFile}
                        isDisabled={!appContext.isDraft()}
                    />
                </Tooltip>
            ) : null}
        </Flex>
        {!currentSoundFile ? (
            <HStack spacing={3}>
                <Button size={"sm"}
                        onClick={() => openDialog()}
                        isDisabled={!appContext.isDraft()}>
                    Upload / select file
                </Button>
            </HStack>
        ) : null}

        {currentSoundFile ? (
            <SoundFileDetails
                key={`sound_${currentSoundFile.fileName || language.code}`}
                spot={spot}
                soundFile={{
                    ...currentSoundFile,
                    ambient: isAmbient,
                    ...(isDefaultRow
                        ? (defaultRowUsesLanguageCode && resolvedDefaultLanguageCode ? {language: resolvedDefaultLanguageCode} : {})
                        : {language: language.code})
                }}
                libraryEntry={currentSoundFile.fileName ? libraryByObjectId.get(currentSoundFile.fileName) : undefined}
                language={language.code}
                isDefaultLanguage={isDefaultRow}
                isAmbientSlot={isAmbient}
                defaultLanguageCode={resolvedDefaultLanguageCode}
                defaultRowUsesLanguageCode={defaultRowUsesLanguageCode}
                onReplace={() => openDialog(currentSoundFile?.fileName)}
            />
        ) : (
            <Text fontSize={"sm"} color={"gray.600"}>No sound file assigned.</Text>
        )}

        <SoundFileLibraryDialog
            isOpen={isDialogOpen}
            onClose={() => setDialogOpen(false)}
            mode={"slot"}
            existingSoundOptions={existingSoundOptions}
            isLoading={isLibraryLoading}
            selectedExistingFile={selectedExistingFile}
            onSelectExistingFile={setSelectedExistingFile}
            onAddExisting={addExistingSoundFile}
            onUpload={(originalFileName: string, fileName: string, filePath: string, metadata?: UploadFileMetadata) => {
                addOrReplaceSoundFile(originalFileName, fileName, filePath, metadata);
            }}
            registerUploadedFile={registerUploadedFile}
            enqueueUploads={enqueueUploads}
            isReplace={!!currentSoundFile}
        />
    </Box>;
};

export default SoundSlotSection;
